export const ARBITRUM_CHAIN_ID         = 42161;
export const ARBITRUM_SEPOLIA_CHAIN_ID = 421614;

// ── Contracts ────────────────────────────────────────────────────────────────

const _CONTRACTS = {
  // GMX v2.2c (v2.2b was disabled 2026-09-14 — calls to it revert Unauthorized).
  // Source: github.com/gmx-io/gmx-synthetics docs/arbitrum-deployments.md
  // Router is the token-approval spender; ExchangeRouter pulls through it.
  [ARBITRUM_CHAIN_ID]: {
    ExchangeRouter: '0x7dE39FF2e232A2203196788d37e234cF8F1b83f1',
    Router:         '0x7452c558d45f8afC8c83dAe62C3f8A5BE19c71f6',
    OrderVault:     '0x31eF83a530Fde1B38EE9A18093A333D8Bbbc40D5',
    Reader:         '0xfA26cBb46e2614609406de08CA1Dc7f70a684184',
    DataStore:      '0xFD70de6b91282D8017aA4E741e9Ae325CAb992d8',
  },
  [ARBITRUM_SEPOLIA_CHAIN_ID]: {
    ExchangeRouter: '0x6B489dD5bB1AAE8df246359d59aA7316760a75d2',
    Router:         '0x72F13a44C8ba16a678CAD549F17bc9e06d2B8bD2',
    OrderVault:     '0x1b8AC606de71686fd2a1AEDEcb6E0EFba28909a2',
    Reader:         '0x92659fEf40582ceCC3CBa4D096d28291C238D358',
    DataStore:      '0xCF4c2C4c53157BcC01A596e3788fFF69cBBCD201',
  },
};

// ── Tokens ───────────────────────────────────────────────────────────────────

const _TOKENS = {
  [ARBITRUM_CHAIN_ID]: {
    USDC: { address: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', decimals: 6,  symbol: 'USDC' },
    WETH: { address: '0x82aF49447D8a07e3bd95BD0d56f35241523fBab1', decimals: 18, symbol: 'WETH' },
    WBTC: { address: '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f', decimals: 8,  symbol: 'WBTC' },
  },
  [ARBITRUM_SEPOLIA_CHAIN_ID]: {
    USDC: { address: '0x3253a335E7bFfB4790Aa4C25C4250d206E9b9773', decimals: 6,  symbol: 'USDC' },
    WETH: { address: '0x980B62Da83eFf3D4576C647993b0c1D7faf17c73', decimals: 18, symbol: 'WETH' },
    WBTC: { address: '0xF79cE1Cf38A09D572b021B4C5548b75A14082F12', decimals: 8,  symbol: 'BTC'  },
  },
};

// ── Markets ──────────────────────────────────────────────────────────────────

const ETH_PYTH = '0xff61491a931112ddf1bd8147cd1b641375f79f5825126d665480874634fd0ace';
const BTC_PYTH = '0xe62df6c8b4a85fe1a67db44dc12de5db330f7ac66b72dc658afedf0f4a415b43';

const _MARKETS = {
  [ARBITRUM_CHAIN_ID]: {
    'ETH/USD': {
      marketToken:         '0x70d95587d40A2caf56bd97485aB3Eec10Bee6336',
      indexToken:          _TOKENS[ARBITRUM_CHAIN_ID].WETH.address,
      longToken:           _TOKENS[ARBITRUM_CHAIN_ID].WETH.address,
      shortToken:          _TOKENS[ARBITRUM_CHAIN_ID].USDC.address,
      cgId:                'ethereum',
      symbol:              'ETH',
      pricePrecision:      2,
      pythFeedId:          ETH_PYTH,
      indexTokenDecimals:  18,
    },
    'BTC/USD': {
      marketToken:         '0x47c031236e19d024b42f8AE6780E44A573170703',
      indexToken:          '0x47904963fc8b2340414262125aF798B9655E58Cd', // synthetic BTC price index (not WBTC)
      longToken:           _TOKENS[ARBITRUM_CHAIN_ID].WBTC.address,
      shortToken:          _TOKENS[ARBITRUM_CHAIN_ID].USDC.address,
      cgId:                'bitcoin',
      symbol:              'BTC',
      pricePrecision:      0,
      pythFeedId:          BTC_PYTH,
      indexTokenDecimals:  8,
    },
  },
  [ARBITRUM_SEPOLIA_CHAIN_ID]: {
    'ETH/USD': {
      marketToken:         '0xb6fC4C9eB02C35A134044526C62bb15014Ac0Bcc',
      indexToken:          _TOKENS[ARBITRUM_SEPOLIA_CHAIN_ID].WETH.address,
      longToken:           _TOKENS[ARBITRUM_SEPOLIA_CHAIN_ID].WETH.address,
      shortToken:          _TOKENS[ARBITRUM_SEPOLIA_CHAIN_ID].USDC.address,
      cgId:                'ethereum',
      symbol:              'ETH',
      pricePrecision:      2,
      pythFeedId:          ETH_PYTH,
      indexTokenDecimals:  18,
    },
    'BTC/USD': {
      marketToken:         '0x3A83246bDDD60c4e71c91c10D9A66Fd64399bBCf',
      indexToken:          _TOKENS[ARBITRUM_SEPOLIA_CHAIN_ID].WBTC.address,
      longToken:           _TOKENS[ARBITRUM_SEPOLIA_CHAIN_ID].WBTC.address,
      shortToken:          _TOKENS[ARBITRUM_SEPOLIA_CHAIN_ID].USDC.address,
      cgId:                'bitcoin',
      symbol:              'BTC',
      pricePrecision:      0,
      pythFeedId:          BTC_PYTH,
      indexTokenDecimals:  8,
    },
  },
};

// ── Chain-aware config resolver ───────────────────────────────────────────────

export function getNetworkConfig(chainId) {
  const id = chainId === ARBITRUM_SEPOLIA_CHAIN_ID
    ? ARBITRUM_SEPOLIA_CHAIN_ID
    : ARBITRUM_CHAIN_ID;
  return {
    contracts: _CONTRACTS[id],
    tokens:    _TOKENS[id],
    markets:   _MARKETS[id],
    isTestnet: id === ARBITRUM_SEPOLIA_CHAIN_ID,
  };
}

// Mainnet defaults — backwards-compatible for anything not yet chain-aware
export const GMX_CONTRACTS = _CONTRACTS[ARBITRUM_CHAIN_ID];
export const TOKENS        = _TOKENS[ARBITRUM_CHAIN_ID];
export const GMX_MARKETS   = _MARKETS[ARBITRUM_CHAIN_ID];

// ── Order type enums ──────────────────────────────────────────────────────────

export const ORDER_TYPE = {
  MarketSwap:        0,
  LimitSwap:         1,
  MarketIncrease:    2,
  LimitIncrease:     3,
  MarketDecrease:    4,
  LimitDecrease:     5,
  StopLossDecrease:  6,
  Liquidation:       7,
};

// ── Fee constants ─────────────────────────────────────────────────────────────

export const INCREASE_ORDER_GAS_LIMIT    = 4_200_000;  // fallback only — the live value comes from GMX's DataStore
// Only used when the gas price can't be read at all. The live estimate is
// gasPrice x INCREASE_ORDER_GAS_LIMIT x 1.3, which at Arbitrum's ~0.02 gwei is
// ~0.00005 ETH — the old flat 0.0002 floor overstated it ~4x and blocked wallets
// that held several times the real fee. GMX refunds whatever the keeper doesn't spend.
export const EXECUTION_FEE_FALLBACK_WEI  = '150000000000000'; // 0.00015 ETH
export const EXECUTION_FEE_MIN_WEI       = '60000000000000';  // 0.00006 ETH — floor against a gas lull

// ── Misc ──────────────────────────────────────────────────────────────────────

export const GMX_SUBGRAPH_URL = {
  [ARBITRUM_CHAIN_ID]:         'https://subgraph.satsuma-prod.com/3b2ced13c8d9/gmx/synthetics-arbitrum/api',
  [ARBITRUM_SEPOLIA_CHAIN_ID]: 'https://subgraph.satsuma-prod.com/3b2ced13c8d9/gmx/synthetics-arbitrum-sepolia/api',
};

export const UI_FEE_RECEIVER = '0x0000000000000000000000000000000000000000';
export const MARKET_SLIPPAGE          = 0.01; // open orders — 1%
export const DECREASE_SLIPPAGE        = 0.05; // close orders — 5% (Pyth oracle can lag CoinGecko)
/*
 * Pyth cross-check: OFF.
 *
 * Pyth gated hermes.pyth.network on 2026-08-26. The public endpoint now 401s and
 * our key is refused with a 403, so the branch cannot succeed — it only costs a
 * 6s timeout on every cross-check where Coinbase AND Chainlink both fail, and
 * puts a permanent red error in the console that masks real failures.
 *
 * Coinbase (primary) and Chainlink (secondary) both work, so nothing is lost.
 * Flip to true once a valid Pyth entitlement is in PYTH_API_KEY, and set
 * PYTH_ENABLED=1 in model_data_in_db/.env to re-open the proxy route.
 */
export const PYTH_CROSSCHECK_ENABLED = false;

export const MIN_POSITION_SIZE_USD = 2;
export const PRICE_PRECISION = '1000000000000000000000000000000'; // 1e30

// Chainlink price feeds on Arbitrum — free on-chain oracle read through the wallet provider,
// used as a backup cross-check source. USD feeds are 8-decimals. BTC/ETH update on a short
// heartbeat (~1–5 min); a freshness gate in the reader rejects any stale answer.
export const CHAINLINK_FEEDS = {
  BTC: '0x6ce185860a4963106506C203335A2910413708e9',
  ETH: '0x639Fe6ab55C921f74e7fac1ee960C0B6293ba612',
};

// chainId: the network the venue trades on. Selecting a venue asks the wallet to
// switch to it — without that the panel mounts on whatever chain the previous
// venue left behind and every on-chain read (balance, allowance, positions)
// silently returns nothing.
export const VENUES = [
  { id: 'hyperliquid', label: 'HYPE',     type: 'dex', chain: 'L1',       logo: null, chainId: 999 },
  { id: 'gmx',         label: 'GMX',      type: 'dex', chain: 'Arbitrum', logo: null, chainId: 42161 },
  { id: 'canborsa',    label: 'PRIVATE',  type: 'dex', chain: null,      logo: null, disabled: true },
];
